import { useEffect, useMemo, useState } from 'react'
import { Search, AlertCircle, CheckCircle2, HelpCircle } from 'lucide-react'
import { apiClient } from '../api/client'
import type { ProtocolConstraint, UUID } from '../types'

interface ConstraintsTabProps {
  studyId: UUID;
}

export default function ConstraintsTab({ studyId }: ConstraintsTabProps) {
  const [constraints, setConstraints] = useState<ProtocolConstraint[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filterMapped, setFilterMapped] = useState<'all' | 'mapped' | 'unmapped'>('all')
  const [filterEntityType, setFilterEntityType] = useState<string>('all')
  const [filterDirection, setFilterDirection] = useState<string>('all')

  useEffect(() => {
    let mounted = true
    apiClient.get<{ constraints: ProtocolConstraint[] }>(`/studies/${studyId}/constraints`)
      .then((data) => {
        if (mounted) {
          setConstraints(data.constraints || [])
          setLoading(false)
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load constraints')
          setLoading(false)
        }
      })
    return () => { mounted = false }
  }, [studyId])

  const entityTypes = useMemo(() => {
    const types = new Set<string>()
    constraints.forEach((c) => { if (c.entity_type) types.add(c.entity_type) })
    return Array.from(types).sort()
  }, [constraints])

  const directionalities = useMemo(() => {
    const dirs = new Set<string>()
    constraints.forEach((c) => { if (c.directionality) dirs.add(c.directionality) })
    return Array.from(dirs).sort()
  }, [constraints])

  const filtered = useMemo(() => {
    return constraints.filter((c) => {
      const matchesSearch =
        search === '' ||
        c.entity_name.toLowerCase().includes(search.toLowerCase()) ||
        c.normalized_constraint_text.toLowerCase().includes(search.toLowerCase()) ||
        c.original_source_text.toLowerCase().includes(search.toLowerCase()) ||
        c.comparator.toLowerCase().includes(search.toLowerCase())
      const mapped = c.status === 'mapped_explicit_protocol_constraint'
      const matchesMapped =
        filterMapped === 'all' ||
        (filterMapped === 'mapped' && mapped) ||
        (filterMapped === 'unmapped' && !mapped)
      const matchesEntity = filterEntityType === 'all' || c.entity_type === filterEntityType
      const matchesDirection = filterDirection === 'all' || c.directionality === filterDirection
      return matchesSearch && matchesMapped && matchesEntity && matchesDirection
    })
  }, [constraints, search, filterMapped, filterEntityType, filterDirection])

  const mappedCount = constraints.filter((c) => c.status === 'mapped_explicit_protocol_constraint').length
  const unmappedCount = constraints.length - mappedCount

  if (loading) return <div className="panel p-6 text-sm text-gray-600">Loading constraints...</div>
  if (error) return <div className="panel p-6 text-sm text-red-600">{error}</div>

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Constraints / Ranges / Thresholds
        </h3>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {constraints.length} total · <span className="text-green-600">{mappedCount} mapped</span> ·{' '}
          <span className="text-amber-600">{unmappedCount} unmapped</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 panel p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search constraints..."
            className="w-full pl-9 pr-3 py-1.5 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          />
        </div>
        <select
          value={filterMapped}
          onChange={(e) => setFilterMapped(e.target.value as any)}
          className="text-sm border rounded px-3 py-1.5 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
        >
          <option value="all">Mapped + Unmapped</option>
          <option value="mapped">Mapped only</option>
          <option value="unmapped">Unmapped only</option>
        </select>
        <select
          value={filterEntityType}
          onChange={(e) => setFilterEntityType(e.target.value)}
          className="text-sm border rounded px-3 py-1.5 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
        >
          <option value="all">All entity types</option>
          {entityTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          value={filterDirection}
          onChange={(e) => setFilterDirection(e.target.value)}
          className="text-sm border rounded px-3 py-1.5 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
        >
          <option value="all">All directionalities</option>
          {directionalities.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="panel p-8 text-center text-sm text-gray-500">
          No constraints match the current filters.
        </div>
      ) : (
        <div className="overflow-x-auto panel">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Entity</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Constraint</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Comparator</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Bounds / Window</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Direction</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Source</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.map((c) => (
                <tr key={c.constraint_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3 align-top">
                    <div className="font-medium text-gray-900 dark:text-white">{c.entity_name || '—'}</div>
                    <div className="text-xs text-gray-500">{c.entity_type || 'unknown'}</div>
                    {c.mapped_item && (
                      <div className="text-xs text-gray-500 mt-1">
                        item: {c.mapped_item}
                        {c.mapped_form && <span> · form: {c.mapped_form}</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top max-w-md">
                    <div className="text-gray-900 dark:text-gray-100">{c.normalized_constraint_text || c.original_source_text}</div>
                    {c.categorical_allowed_values?.length > 0 && (
                      <div className="text-xs text-gray-500 mt-1">allowed: {c.categorical_allowed_values.join(', ')}</div>
                    )}
                    {c.temporal_window && (
                      <div className="text-xs text-gray-500 mt-1">window: {c.temporal_window}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top font-mono text-gray-700 dark:text-gray-300">{c.comparator || '—'}</td>
                  <td className="px-4 py-3 align-top">
                    {c.lower_bound !== null && c.upper_bound !== null ? (
                      <span>{c.lower_inclusive ? '[' : '('}{c.lower_bound}, {c.upper_bound}{c.upper_inclusive ? ']' : ')'}</span>
                    ) : c.lower_bound !== null ? (
                      <span>{c.comparator} {c.lower_bound} {c.unit}</span>
                    ) : c.upper_bound !== null ? (
                      <span>{c.comparator} {c.upper_bound} {c.unit}</span>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top capitalize">{c.directionality?.replace(/_/g, ' ') || '—'}</td>
                  <td className="px-4 py-3 align-top text-xs text-gray-500">
                    {c.source_section || '—'}
                    {c.source_page && <span> · p.{c.source_page}</span>}
                  </td>
                  <td className="px-4 py-3 align-top">
                    {c.status === 'mapped_explicit_protocol_constraint' ? (
                      <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-300 text-xs font-medium">
                        <CheckCircle2 className="h-3.5 w-3.5" /> mapped
                      </span>
                    ) : c.status === 'ambiguous_constraint' ? (
                      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300 text-xs font-medium">
                        <HelpCircle className="h-3.5 w-3.5" /> ambiguous
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-slate-700 dark:text-slate-300 text-xs font-medium">
                        <AlertCircle className="h-3.5 w-3.5" /> {c.status?.replace(/_/g, ' ')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
